import React, { useState, useEffect, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import { useDispatch, useSelector } from 'react-redux';
import HeaderComponent from '../components/HeaderComponent';
import { Box, Typography, TableBody, TableCell, TableHead, TableRow, Paper, Tooltip, Button } from '@mui/material';
import { getDashboardManagerSummaryData, setDashboardTimePeriod, getDashboardMonthlyTrendsData, getDashboardPendingDetailsCandidatesData } from '../redux/actions/commonActions';
import StickyTableContainer from '../components/StickyTableContainer';
import { setEmployerFilters } from '../redux/actions/employerActions';
import { useNavigate } from 'react-router-dom';
import { addJobPostsToPriorityQueue, getPriorityJobsList, removeJobPostFromPriorityQueue, setJobsFilters } from '../redux/actions/jobActions';
import { AccessTimeFilled, Badge, Call, Cancel, ChangeCircle, CloseFullscreen, CurrencyExchange, Flag, OpenInFull, Percent, Person, PersonAddAlt1, PersonOff, Repeat, ScheduleSend, TaskAlt, ThumbUp, Timer, Whatshot, WhatshotOutlined, Work } from '@mui/icons-material';
import PipelineCandidatesModal from '../models/PipelineCandidatesModal';
import TimePeriodSelector from '../components/TimePeriodSelector';
import SummaryCard from '../components/SummaryCard';
import { pipelineBgColors } from '../helpers/dashboardColors';
import GoalModal from '../models/GoalModal';
import constants from '../helpers/constants.json';
import StickyNotesComponent from '../components/StickyNotesComponent';
import PendingJoiningDetails from '../components/PendingJoiningDetails';
import MonthlyTrendsChart from '../components/MonthlyTrendsChart';

const ViewManagerDashboard = (props) => {

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { managerId } = props;

  const [jobPostId, setJobPostId] = useState(null);
  const [isPipelineCandidatesModalOpen, setIsPipelineCandidatesModalOpen] = useState(false);
  const [selectedPipelineStatus, setSelectedPipelineStatus] = useState("ALL");
  const [isGoalModalVisible, setIsGoalModalVisible] = useState(false);
  const [expandPriorityJobsView, setExpandPriorityJobsView] = useState(false);

  const dashboardTimePeriod = useSelector((state) => state.common.dashboardTimePeriod);
  const activeManager = useSelector((state) => state.common.activeManager);

  const dashboardManagerSummaryData = useSelector((state) => state.common.dashboardManagerSummaryData);
  const dashboardMonthlyTrendsData = useSelector((state) => state.common.dashboardMonthlyTrendsData);
  const dashboardPendingDetailsCandidates = useSelector((state) => state.common.dashboardPendingDetailsCandidates);

  const priorityJobs = useSelector((state) => state.job.priorityJobs);

  const employerFilters = useSelector((state) => state.employer.employerFilters);
  const jobsFilters = useSelector((state) => state.job.jobsFilters);

  const mainStyle = {
    divStyle: {
      display: 'flex',
      justifyContent: 'flex-start',
      padding: '4px'
    },
    spanStyle: {
      color: 'gray',
      textAlign: 'right',
      marginRight: '8px',
      minWidth: '140px',
      // width: '100px',
      fontSize: '14px'
    }
  };

  useEffect(() => {
    if (managerId == "overall") {
      dispatch(getDashboardMonthlyTrendsData());
      dispatch(getDashboardPendingDetailsCandidatesData());
    } else {
      let aParams = { 'managerId': managerId };
      dispatch(getPriorityJobsList(managerId));
      dispatch(getDashboardMonthlyTrendsData(aParams));
      dispatch(getDashboardPendingDetailsCandidatesData(aParams));
    }

  }, [managerId]);

  useEffect(() => {
    let aParams = { ...dashboardTimePeriod };
    if (managerId == "overall") {
      dispatch(getDashboardManagerSummaryData(aParams));
    } else {
      aParams['managerId'] = managerId;
      dispatch(getDashboardManagerSummaryData(aParams))
    }

  }, [dashboardTimePeriod]);

  const numericCols = ["totalInPipeline", "totalInProcess", "totalInterviewed", "totalSelected", "totalRejected", "totalJoined", "totalOthers"];

  // ✅ Precompute highlight index once using useMemo
  const processedRows = useMemo(() => {
    return priorityJobs && priorityJobs.map((row) => {
      const lastPositiveIndex = numericCols.findLastIndex((col) => row['pipelineSummary'][col] > 0);
      return { ...row, highlightIndex: lastPositiveIndex };
    });
  }, [priorityJobs]);

  const handleEmployersClick = () => {
    let empFilters = { ...employerFilters };

    empFilters['refundAccount'] = '';
    empFilters['zeroLineupClient'] = '';

    empFilters['managerId'] = managerId;
    empFilters['activeClient'] = true;
    empFilters['isApplied'] = true;
    dispatch(setEmployerFilters(empFilters));
    navigate(`/app/employers`);
  }

  const handleJobPostsClick = () => {
    let jFilters = { ...jobsFilters };

    // TODO: optimize this, clear high ageing job filters
    jFilters['highAgeingJob'] = '';
    jFilters['replacementDue'] = '';
    jFilters['lineup'] = '';
    jFilters['interviewed'] = '';
    jFilters['rejected'] = '';
    jFilters['joined'] = '';
    jFilters['replaced'] = '';
    jFilters['fromStatusDate'] = '';
    jFilters['toStatusDate'] = '';

    jFilters['managerId'] = managerId;
    jFilters['trackerView'] = true;
    jFilters['active'] = true;
    jFilters['jobExpired'] = false;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleHighAgeingJobPostsClick = () => {
    let jFilters = { ...jobsFilters };

    jFilters['replacementDue'] = '';
    jFilters['lineup'] = '';
    jFilters['interviewed'] = '';
    jFilters['rejected'] = '';
    jFilters['joined'] = '';
    jFilters['replaced'] = '';
    jFilters['fromStatusDate'] = '';
    jFilters['toStatusDate'] = '';

    jFilters['highAgeingJob'] = true;
    jFilters['active'] = true;
    jFilters['jobExpired'] = false;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleRefundAccountsClick = () => {
    let empFilters = { ...employerFilters };
    empFilters['zeroLineupClient'] = '';
    empFilters['activeClient'] = '';

    empFilters['refundAccount'] = true;
    empFilters['managerId'] = managerId;
    empFilters['isApplied'] = true;
    dispatch(setEmployerFilters(empFilters));
    navigate(`/app/employers`);
  }

  const handleZeroLineupClientClick = () => {
    let empFilters = { ...employerFilters };
    empFilters['refundAccount'] = '';
    empFilters['activeClient'] = '';

    empFilters['zeroLineupClient'] = true;
    empFilters['managerId'] = managerId;
    empFilters['isApplied'] = true;
    dispatch(setEmployerFilters(empFilters));
    navigate(`/app/employers`);
  }

  const handleReplacementDuesClick = () => {
    let jFilters = { ...jobsFilters };

    jFilters['highAgeingJob'] = '';
    jFilters['jobExpired'] = '';
    jFilters['lineup'] = '';
    jFilters['interviewed'] = '';
    jFilters['rejected'] = '';
    jFilters['joined'] = '';
    jFilters['replaced'] = '';
    jFilters['fromStatusDate'] = '';
    jFilters['toStatusDate'] = '';

    jFilters['replacementDue'] = true;
    jFilters['active'] = true;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleLineupClick = () => {
    let jFilters = { ...jobsFilters };

    jFilters['highAgeingJob'] = '';
    jFilters['jobExpired'] = '';
    jFilters['active'] = '';
    jFilters['replacementDue'] = '';
    jFilters['interviewed'] = '';
    jFilters['rejected'] = '';
    jFilters['replaced'] = '';

    jFilters['lineup'] = true;
    jFilters['fromStatusDate'] = dashboardTimePeriod.fromDate;
    jFilters['toStatusDate'] = dashboardTimePeriod.toDate;
    jFilters['trackerView'] = true;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleInterviewedClick = () => {
    let jFilters = { ...jobsFilters };

    jFilters['highAgeingJob'] = '';
    jFilters['jobExpired'] = '';
    jFilters['active'] = '';
    jFilters['replacementDue'] = '';
    jFilters['lineup'] = '';
    jFilters['rejected'] = '';
    jFilters['replaced'] = '';

    jFilters['interviewed'] = true;
    jFilters['fromStatusDate'] = dashboardTimePeriod.fromDate;
    jFilters['toStatusDate'] = dashboardTimePeriod.toDate;
    jFilters['trackerView'] = true;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleRejectedClick = () => {
    let jFilters = { ...jobsFilters };

    jFilters['highAgeingJob'] = '';
    jFilters['jobExpired'] = '';
    jFilters['active'] = '';
    jFilters['replacementDue'] = '';
    jFilters['lineup'] = '';
    jFilters['interviewed'] = '';
    jFilters['replaced'] = '';

    jFilters['rejected'] = true;
    jFilters['fromStatusDate'] = dashboardTimePeriod.fromDate;
    jFilters['toStatusDate'] = dashboardTimePeriod.toDate;
    jFilters['trackerView'] = true;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleJoinedClick = () => {
    let jFilters = { ...jobsFilters };

    jFilters['highAgeingJob'] = '';
    jFilters['jobExpired'] = '';
    jFilters['active'] = '';
    jFilters['replacementDue'] = '';
    jFilters['lineup'] = '';
    jFilters['interviewed'] = '';
    jFilters['rejected'] = '';
    jFilters['replaced'] = '';

    jFilters['joined'] = true;
    jFilters['fromStatusDate'] = dashboardTimePeriod.fromDate;
    jFilters['toStatusDate'] = dashboardTimePeriod.toDate;
    jFilters['trackerView'] = true;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handleReplacedClick = (e) => {
    e.stopPropagation();

    let jFilters = { ...jobsFilters };

    jFilters['highAgeingJob'] = '';
    jFilters['jobExpired'] = '';
    jFilters['active'] = '';
    jFilters['replacementDue'] = '';
    jFilters['lineup'] = '';
    jFilters['interviewed'] = '';
    jFilters['rejected'] = '';
    jFilters['joined'] = '';

    jFilters['replaced'] = true;
    jFilters['fromStatusDate'] = dashboardTimePeriod.fromDate;
    jFilters['toStatusDate'] = dashboardTimePeriod.toDate;
    jFilters['trackerView'] = true;
    jFilters['managerId'] = managerId;
    jFilters['isApplied'] = true;
    dispatch(setJobsFilters(jFilters));
    navigate(`/app/jobs`);
  }

  const handlePipelineStatusClick = (jobPostId, pipelineStatus) => {
    setJobPostId(jobPostId);
    setSelectedPipelineStatus(pipelineStatus);
    setIsPipelineCandidatesModalOpen(true);
  }

  const handleViewCandidatePipeline = (jobPostId) => {
    setJobPostId(jobPostId);
    setIsPipelineCandidatesModalOpen(true);
  }

  const handleClosePipelineCandidatesModal = () => {
    setIsPipelineCandidatesModalOpen(false);
    setSelectedPipelineStatus("ALL");
  }

  const handleTimePeriodChange = (e) => {
    let aParams = {
      'rangeType': e.rangeType,
      "fromDate": e.from,
      "toDate": e.to
    }
    dispatch(setDashboardTimePeriod(aParams));
  }

  const handleJobPriorityQueueButton = (job) => {
    let jobPostId = job.jobPostId;
    if (job.inPriorityQueue) {
      dispatch(removeJobPostFromPriorityQueue(managerId, jobPostId));
    } else {
      let aParams = {
        'jobPostIds': [jobPostId]
      }

      dispatch(addJobPostsToPriorityQueue(managerId, aParams));
    }
  }

  const handleExpandPriorityJobsView = () => {
    setExpandPriorityJobsView(!expandPriorityJobsView);
  }

  const handleGoalsClick = () => {
    setIsGoalModalVisible(true);
  }

  const handleCloseGoalModal = () => {
    setIsGoalModalVisible(false);
  }

  const goalProgressRounded = Math.round((dashboardManagerSummaryData?.goalAchievedValue * 100) / dashboardManagerSummaryData?.goalValue);

  const overviewCardsData = [
    {
      title: "Active Clients",
      value: dashboardManagerSummaryData?.totalActiveEmployers,
      secondValue: `App: ${dashboardManagerSummaryData?.totalActiveAppEmployers}`,
      thirdValue: `Assisted: ${dashboardManagerSummaryData?.totalActiveAssistedHireEmployers}`,
      onClick: managerId != 'overall' ? handleEmployersClick : null,
      icon: <Badge />
    },
    {
      title: "Open Positions",
      value: dashboardManagerSummaryData?.totalActiveJobPostsOverall,
      secondValue: `New Jobs: ${dashboardManagerSummaryData?.totalNewJobPosts}`,
      // trendPercentage: dashboardManagerSummaryData?.jobPostsTrendRate,
      onClick: managerId != 'overall' ? handleJobPostsClick : null,
      icon: <Work />
    },
    {
      title: "Line ups",
      value: dashboardManagerSummaryData?.totalLineups,
      onClick: managerId != 'overall' ? handleLineupClick : null,
      icon: <Person />
    },
    {
      title: "Interviewed",
      value: dashboardManagerSummaryData?.totalInterviewed,
      trendPercentage: dashboardManagerSummaryData?.interviewedTrendRate,
      onClick: managerId != 'overall' ? handleInterviewedClick : null,
      icon: <Call />
    },
    {
      title: "Joined",
      value: dashboardManagerSummaryData?.totalJoined,
      trendPercentage: dashboardManagerSummaryData?.joinedTrendRate,
      clickableItem: { title: 'Replacements', value: dashboardManagerSummaryData?.totalReplaced, onClick: managerId != 'overall' ? handleReplacedClick : null },
      onClick: managerId != 'overall' ? handleJoinedClick : null,
      icon: <ThumbUp />
    },
    {
      title: "Rejected",
      value: dashboardManagerSummaryData?.totalRejected,
      onClick: managerId != 'overall' ? handleRejectedClick : null,
      icon: <Cancel />
    },
    {
      title: "Candidates Added",
      value: dashboardManagerSummaryData?.totalCandidatesAdded,
      secondValue: `CV Parsed: ${dashboardManagerSummaryData?.totalCVParsed}`,
      trendPercentage: dashboardManagerSummaryData?.candidatesAddedTrendRate,
      icon: <PersonAddAlt1 />
    },
    {
      title: "Goal",
      value: dashboardManagerSummaryData?.goalValue != 0 && dashboardManagerSummaryData?.goalValue,
      secondValue: dashboardManagerSummaryData?.goalValue == 0 && 'Click to Set Goal',
      progressValue: dashboardManagerSummaryData?.goalValue != 0 && goalProgressRounded,
      onClick: managerId != 'overall' ? handleGoalsClick : null,
      icon: <Flag />
    },
    {
      title: "Refund Accounts",
      value: dashboardManagerSummaryData?.totalRefundAccounts,
      secondValue: dashboardManagerSummaryData?.totalRefundAccounts > 0 && `Pending: ${dashboardManagerSummaryData?.totalPendingRefunds}`,
      thirdValue: dashboardManagerSummaryData?.totalRefundAccounts > 0 && `Processed: ${dashboardManagerSummaryData?.totalProcessedRefunds}`,
      onClick: managerId != 'overall' ? handleRefundAccountsClick : null,
      icon: <CurrencyExchange />
    }
  ];

  const opOverviewCardsData = [
    {
      title: "High Ageing Jobs",
      value: dashboardManagerSummaryData?.totalHighAgeingJobs,
      onClick: managerId != 'overall' ? handleHighAgeingJobPostsClick : null,
      icon: <AccessTimeFilled />
    },
    {
      title: "Replacement Dues",
      value: dashboardManagerSummaryData?.totalReplacementDues,
      onClick: managerId != 'overall' ? handleReplacementDuesClick : null,
      icon: <ChangeCircle />
    },
    {
      title: "Zero Lineup Clients",
      value: dashboardManagerSummaryData?.totalZeroLineupEmployers,
      onClick: managerId != 'overall' ? handleZeroLineupClientClick : null,
      icon: <PersonOff />
    },
    {
      title: "Conversion %",
      value: dashboardManagerSummaryData?.conversionRate,
      icon: <Percent />
    },
    {
      title: "1st Interview Duration",
      value: dashboardManagerSummaryData?.firstInterviewDuration,
      icon: <Timer />
    },
    {
      title: "TAT Profile Shared",
      value: dashboardManagerSummaryData?.tatProfileShared,
      icon: <ScheduleSend />
    },
    {
      title: "TAT Candidate Hired",
      value: dashboardManagerSummaryData?.tatCandidateHired,
      icon: <TaskAlt />
    },
    {
      title: "30 Days Retention",
      value: dashboardManagerSummaryData?.totalJoinedIncludingLeft > 0 ? `${dashboardManagerSummaryData?.thirtyDaysRetention}%` : '--',
      secondValue: `Joined: ${dashboardManagerSummaryData?.totalJoinedIncludingLeft}`,
      thirdValue: `Left: ${dashboardManagerSummaryData?.totalLeftCandidatesWithin30Days}`,
      icon: <Repeat />
    },
  ]

  return (
    <>
      <HeaderComponent name={`${activeManager ? activeManager.name : 'Manager'}'s Dashboard`} />
      <div style={{ marginTop: '12px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', paddingBottom: '4px' }}>
          <Typography variant='h6' sx={{ fontWeight: 'bold', color: '#3d3d3d', padding: '8px 0', marginLeft: '8px' }}>Overview</Typography>
          <TimePeriodSelector activeItem={dashboardTimePeriod.rangeType} onChange={handleTimePeriodChange} />
        </div>
        <Grid container spacing={3}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              // justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            {overviewCardsData.map(card =>
              <SummaryCard
                key={card.title}
                title={card.title}
                value={card.value}
                secondValue={card.secondValue}
                thirdValue={card.thirdValue}
                progressValue={card.progressValue}
                trendPercentage={card.trendPercentage}
                clickableItem={card.clickableItem}
                onClick={card.onClick}
                icon={card.icon}
              />
            )}
          </Box>
        </Grid>
      </div>

      <Paper elevation={2} style={{ marginTop: '16px' }}>
        <Box sx={{ width: '100%' }}>
          <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant='h6' sx={{ fontWeight: 'bold', color: '#3d3d3d', padding: '8px 0', marginLeft: '8px' }}>Priority Jobs ({processedRows?.length})</Typography>
            <Tooltip title={expandPriorityJobsView ? "Collapse" : "Expand"}>
              <Button style={{ minWidth: '32px', marginRight: '12px' }} onClick={handleExpandPriorityJobsView}>
                {expandPriorityJobsView ? <CloseFullscreen /> : <OpenInFull />}
              </Button>
            </Tooltip>
          </div>
          {processedRows && processedRows.length > 0 ?
            <StickyTableContainer height={expandPriorityJobsView ? 500 : 250}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ padding: '2px' }}></TableCell>
                  <TableCell style={styles.textBold}>Job Title</TableCell>
                  <TableCell style={styles.textBold}>Location</TableCell>
                  <TableCell style={styles.textBold}>Employer</TableCell>
                  {constants.jobPipelineStatusDashboard.map((status, i) =>
                    <TableCell key={status.id} style={styles.textBold}>
                      {status.name}
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {processedRows && processedRows.map((jobPost) => (
                  <TableRow
                    key={jobPost.jobPostId}
                  >
                    <TableCell style={{ padding: '2px' }}>
                      <Tooltip title={jobPost.inPriorityQueue ? "Remove from Priority Queue" : "Add to Priority Queue"}>
                        <Button style={{ minWidth: '32px' }} color="primary" onClick={(e) => { handleJobPriorityQueueButton(jobPost) }}>
                          {jobPost.inPriorityQueue ?
                            <>
                              <Whatshot style={{ color: '#FF8C00', position: 'absolute' }} />
                              <WhatshotOutlined style={{ color: '#ffd700' }} />
                            </>
                            :
                            <WhatshotOutlined />
                          }
                        </Button>
                      </Tooltip></TableCell>
                    <TableCell
                      style={{ cursor: 'pointer' }}
                      onClick={() => { handleViewCandidatePipeline(jobPost.jobPostId) }}>{jobPost.jobTitle}</TableCell>
                    <TableCell>{jobPost.jobLocation}</TableCell>
                    <TableCell>{jobPost.employerBrandName}</TableCell>
                    {constants.jobPipelineStatusDashboard.map((status, i) => {
                      return (
                        <TableCell
                          key={status.id}
                          style={{
                            padding: '0px'
                          }}
                        >
                          <div
                            style={{
                              textAlign: 'center',
                              fontWeight: 'bold',
                              padding: '8px 0px',
                              backgroundColor: jobPost['pipelineSummary'][constants.jobPipelineStatusMap[status.id]] != 0 && pipelineBgColors[i],
                              marginLeft: '-8px',
                              transition: "background-color 0.3s ease",
                              clipPath: "polygon(0 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 0 100%, 10px 50%)",
                              cursor: jobPost['pipelineSummary'][constants.jobPipelineStatusMap[status.id]] != 0 && 'pointer'
                            }}
                            onClick={() => jobPost['pipelineSummary'][constants.jobPipelineStatusMap[status.id]] != 0 && handlePipelineStatusClick(jobPost.jobPostId, status.id)}
                          >
                            {jobPost['pipelineSummary'][constants.jobPipelineStatusMap[status.id]] != 0 && jobPost['pipelineSummary'][constants.jobPipelineStatusMap[status.id]]}
                          </div>
                        </TableCell>
                      )
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </StickyTableContainer>
            :
            <div style={{ display: 'flex', padding: '16px 12px' }}>
              No Jobs found
            </div>
          }
        </Box>
      </Paper>

      <div style={{ marginTop: '16px' }}>
        <Typography variant='h6' sx={{ fontWeight: 'bold', color: '#3d3d3d', padding: '8px 0', marginLeft: '8px' }}>Operational Metrics</Typography>
        <Grid container spacing={3}>
          <Box
            sx={{
              flex: 1,
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            {opOverviewCardsData.map(card =>
              <SummaryCard
                key={card.title}
                title={card.title}
                value={card.value}
                secondValue={card.secondValue}
                thirdValue={card.thirdValue}
                onClick={card.onClick}
                icon={card.icon}
              />
            )}
          </Box>
        </Grid>
      </div>


      <Grid container spacing={2} style={{ marginTop: '16px' }}>
        <Grid size={4}>
          <Paper elevation={2}>
            <MonthlyTrendsChart
              monthlyTrendsData={dashboardMonthlyTrendsData}
            />
          </Paper>
        </Grid>
        <Grid size={4}>
          <Paper elevation={2}>
            <PendingJoiningDetails
              pendingCandidates={dashboardPendingDetailsCandidates}
            />
          </Paper>
        </Grid>
        <Grid size={4}>
          <Paper elevation={2}>
            <StickyNotesComponent height={'298px'} />
          </Paper>
        </Grid>
      </Grid>


      {isPipelineCandidatesModalOpen &&
        <PipelineCandidatesModal
          jobPostId={jobPostId}
          selectedStatus={selectedPipelineStatus}
          isOpen={true}
          onClose={handleClosePipelineCandidatesModal}
        />
      }

      {isGoalModalVisible &&
        <GoalModal
          open={true}
          onClose={handleCloseGoalModal}
          managerId={managerId}
        />
      }
    </>
  );
};

export default ViewManagerDashboard;

const styles = {
  textBold: {
    fontWeight: 'bold'
  }
}